import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../../headers';
import {reAuth} from '../../reAuth';

import {apiBasePaymentLinkV2} from './constants';
import {
  IApplePayMerchantSession,
  IApplePayPaymentRequest,
  IApplePaySubmitResponse,
  ICreatePaymentSessionRequest,
  ICreatePaymentSessionResponse,
  IDefaultPaymentLinkResponse,
  IGetDefaultPaymentLinkResponse,
  IGetPaymentLinkV2Response,
  ILimitsFeesResponse,
  ISubmitPaymentSessionModel,
  ISubmitPaymentSessionResponse,
} from './models';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;
      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);
      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBasePaymentLinkV2.baseUrl;

  const result = await reAuth(args, api, extraOptions, baseUrl, baseQuery);
  return result;
};

export const paymentLinkV2Api = createApi({
  reducerPath: EReducersPath.PAYMENT_LINK_V2_API,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    getLimitFees: build.query<ILimitsFeesResponse, void>({
      query: () => apiBasePaymentLinkV2.endpoints.getLimitFees,
    }),
    getDefaultPaymentLink: build.query<
      IGetDefaultPaymentLinkResponse,
      void | undefined
    >({
      query: () => ({
        url: apiBasePaymentLinkV2.endpoints.getDefaultPaymentLink,
      }),
    }),
    getDefaultPaymentLinkByUserId: build.query<
      IDefaultPaymentLinkResponse,
      string
    >({
      query: (userId: string) =>
        `${apiBasePaymentLinkV2.endpoints.getDefaultPaymentLinkByUserId(userId)}`,
    }),
    getPaymentLinkById: build.query<
      IGetPaymentLinkV2Response,
      {id: string; type?: string}
    >({
      query: ({id, type}) => ({
        url: `${apiBasePaymentLinkV2.endpoints.getPaymentLink}/${id}`,
        params: type ? {type} : undefined,
      }),
    }),
    createPaymentSession: build.mutation<
      ICreatePaymentSessionResponse,
      ICreatePaymentSessionRequest
    >({
      query: data => ({
        url: apiBasePaymentLinkV2.endpoints.createPaymentSession,
        method: 'POST',
        body: data,
      }),
    }),
    submitPaymentSession: build.mutation<
      ISubmitPaymentSessionResponse,
      ISubmitPaymentSessionModel
    >({
      query: data => ({
        url: apiBasePaymentLinkV2.endpoints.submitPaymentSession(
          data.payment_session_id,
        ),
        method: 'POST',
        body: data.submitPaymentSessionData,
      }),
    }),
    validateApplePayMerchant: build.mutation<IApplePayMerchantSession, void>({
      query: () => ({
        url: apiBasePaymentLinkV2.endpoints.validateApplePayMerchant,
        method: 'POST',
      }),
    }),
    submitApplePayPayment: build.mutation<
      IApplePaySubmitResponse,
      IApplePayPaymentRequest
    >({
      query: data => ({
        url: apiBasePaymentLinkV2.endpoints.submitApplePayPayment,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useLazyGetLimitFeesQuery,
  useLazyGetDefaultPaymentLinkQuery,
  useLazyGetDefaultPaymentLinkByUserIdQuery,
  useLazyGetPaymentLinkByIdQuery,
  useCreatePaymentSessionMutation,
  useSubmitPaymentSessionMutation,
  useValidateApplePayMerchantMutation,
  useSubmitApplePayPaymentMutation,
} = paymentLinkV2Api;
